import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DollarSign, TrendingUp, TrendingDown, Wallet, Plus, Loader2, Info, X, AlertCircle, Download } from "lucide-react";
import { useTaxYear } from "../contexts/TaxYearContext";
import TaxYearBadge from "../components/TaxYearBadge";
import ReportActions from "../components/ReportActions";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
};

const EMPTY_FORM = { description: "", amount: "", date: "", category: "Grant Income", notes: "" };

export default function GrantTracking() {
  const { taxYear } = useTaxYear();
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadTxns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/transactions?tax_year=${taxYear}`, { withCredentials: true });
      const all = Array.isArray(data) ? data : [];
      setTxns(all.filter(t => t.category === "Grant Income" || t.category === "Grant Expense"));
    } catch {
      toast.error("Could not load grant transactions");
    } finally {
      setLoading(false);
    }
  }, [taxYear]);

  useEffect(() => { loadTxns(); }, [loadTxns]);

  const totalIncome = txns.filter(t => t.category === "Grant Income").reduce((s, t) => s + (t.amount || 0), 0);
  const totalExpense = txns.filter(t => t.category === "Grant Expense").reduce((s, t) => s + (t.amount || 0), 0);
  const netBalance = totalIncome - totalExpense;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount || !form.date) {
      toast.error("Please fill in description, amount, and date");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/grant-transactions/manual`, {
        ...form,
        amount: parseFloat(form.amount),
      }, { withCredentials: true });
      toast.success("Grant transaction saved");
      setForm(EMPTY_FORM);
      setShowForm(false);
      await loadTxns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #059669, #0d9488)" }}>
            <DollarSign className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Grant Tracking</h1>
            <TaxYearBadge className="ml-2" />
            <p className="text-sm text-slate-500">Track all grant income and grant-funded expenses</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ReportActions
            taxYear={taxYear}
            category="Grant Tracking"
            source="/api/reports/grant-pdf"
            sourceParams={{}}
            filenameHint={`grant-tracking-${taxYear}.pdf`}
            testId="grant-tracking"
          />
          <button
            data-testid="add-grant-txn-btn"
            onClick={() => setShowForm(s => !s)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-all"
            style={{ background: "linear-gradient(135deg, #059669, #0d9488)" }}
          >
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "Cancel" : "Add Transaction"}
          </button>
        </div>
      </div>

      {/* Notices */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            <strong>Taxable notice:</strong> Most childcare grants (Child Care Stabilization, Quality Improvement) are taxable income on Schedule C. Confirm with your accountant.
          </p>
        </div>
        <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl p-3">
          <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-800">
            <strong>CACFP note:</strong> CACFP reimbursements are not grants — they are tracked in CACFP Overview. Do not categorize them as Grant Income here.
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          icon={<TrendingUp className="w-5 h-5 text-emerald-600" />}
          bg="bg-emerald-50"
          label="Total Grant Income"
          value={fmt(totalIncome)}
          sub={`${txns.filter(t => t.category === "Grant Income").length} transactions`}
          testid="grant-total-income"
        />
        <StatCard
          icon={<TrendingDown className="w-5 h-5 text-rose-500" />}
          bg="bg-rose-50"
          label="Total Grant Expenses"
          value={fmt(totalExpense)}
          sub={`${txns.filter(t => t.category === "Grant Expense").length} transactions`}
          testid="grant-total-expense"
        />
        <StatCard
          icon={<Wallet className="w-5 h-5 text-violet-600" />}
          bg="bg-violet-50"
          label="Net Balance"
          value={fmt(netBalance)}
          sub={netBalance >= 0 ? "Surplus" : "Deficit"}
          testid="grant-net-balance"
          highlight={netBalance < 0}
        />
      </div>

      {/* Manual Entry Form */}
      {showForm && (
        <div className="bg-white border border-emerald-200 rounded-2xl shadow-sm p-6">
          <h2 className="font-bold text-slate-900 text-base mb-4" style={{ fontFamily: "Manrope" }}>Add Grant Transaction</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Description *</label>
              <input
                data-testid="grant-form-description"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="e.g. Child Care Stabilization Q2 Disbursement"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Category *</label>
              <select
                data-testid="grant-form-category"
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
              >
                <option value="Grant Income">Grant Income</option>
                <option value="Grant Expense">Grant Expense</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Amount *</label>
              <input
                data-testid="grant-form-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Date *</label>
              <input
                data-testid="grant-form-date"
                type="date"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Notes</label>
              <input
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                data-testid="grant-form-submit"
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #059669, #0d9488)" }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {saving ? "Saving…" : "Save Transaction"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Transactions Table */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-900 text-base" style={{ fontFamily: "Manrope" }}>
            Grant Transactions
            <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 font-semibold px-2 py-0.5 rounded-full">{txns.length}</span>
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">All transactions categorized as Grant Income or Grant Expense — from uploads and manual entries</p>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-6 h-6 text-emerald-500 animate-spin mx-auto mb-2" />
            <p className="text-sm text-slate-500">Loading…</p>
          </div>
        ) : txns.length === 0 ? (
          <div className="p-12 text-center">
            <DollarSign className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-sm font-semibold text-slate-500">No grant transactions yet</p>
            <p className="text-xs text-slate-400 mt-1">Upload a grant document or add a transaction manually above</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="grant-txns-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</th>
                </tr>
              </thead>
              <tbody>
                {txns.sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((t, i) => (
                  <tr key={t._id || t.id || i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(t.date)}</td>
                    <td className="px-4 py-3 text-slate-800 font-medium max-w-xs truncate">{t.description}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        t.category === "Grant Income" ? "bg-emerald-100 text-emerald-700" : "bg-violet-100 text-violet-700"
                      }`}>
                        {t.category}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${
                      t.category === "Grant Income" ? "text-emerald-700" : "text-rose-600"
                    }`}>
                      {t.category === "Grant Income" ? "+" : "-"}{fmt(t.amount)}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      {t.document_id === "manual_grant" ? "Manual entry" : "Upload"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, bg, label, value, sub, testid, highlight }) {
  return (
    <div className={`bg-white border ${highlight ? "border-rose-200" : "border-slate-200"} rounded-xl p-4 shadow-sm`} data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>{icon}</div>
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
          <p className={`text-xl font-bold mt-0.5 ${highlight ? "text-rose-600" : "text-slate-900"}`} style={{ fontFamily: "Manrope" }}>{value}</p>
          <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
        </div>
      </div>
    </div>
  );
}
